import { Overlay } from '@angular/cdk/overlay';
import { HttpClient } from '@angular/common/http';
import {
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormControl,
} from '@angular/forms';
import {
  MAT_AUTOCOMPLETE_SCROLL_STRATEGY,
  MatAutocomplete,
  MatAutocompleteModule,
} from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { RouterLink } from '@angular/router';
import { fuseAnimations } from '@fuse/animations/public-api';
import { Subject, debounceTime, filter, map, takeUntil } from 'rxjs';
import Fuse from 'fuse.js';
@Component({
  selector: 'search',
  templateUrl: './search.component.html',
  encapsulation: ViewEncapsulation.None,
  exportAs: 'fuseSearch',
  animations: fuseAnimations,
  imports: [
    MatButtonModule,
    MatIconModule,
    FormsModule,
    MatAutocompleteModule,
    ReactiveFormsModule,
    MatOptionModule,
    RouterLink,
    MatFormFieldModule,
    MatInputModule,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: MAT_AUTOCOMPLETE_SCROLL_STRATEGY,
      useFactory: () => {
        const overlay = inject(Overlay);
        return () => overlay.scrollStrategies.block();
      },
    },
  ],
})
export class SearchComponent implements OnChanges, OnInit, OnDestroy {
  private _elementRef = inject(ElementRef);
  private _httpClient = inject(HttpClient);
  private _renderer2 = inject(Renderer2);

  @Input() appearance: 'basic' | 'bar' = 'basic';
  @Input() debounce = 300;
  @Input() minLength = 2;
  @Output() search: EventEmitter<any> = new EventEmitter<any>();

  opened = false;
  resultSets: any[]; // All fetched data
  filteredResults: any[]; // Filtered quote requests and shipments
  filteredPages: any[]; // Filtered pages
  searchControl: UntypedFormControl = new UntypedFormControl();
  private _matAutocomplete: MatAutocomplete;
  private _unsubscribeAll: Subject<any> = new Subject<any>();
  // Fuse.js instance
  private fuseShipmentsAndQuotes: Fuse<any>; // Fuzzy search for shipments and quotes

  private fusePages: Fuse<any>;

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  /**
   * Constructor
   */
  constructor() {}

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Host binding for component classes
   */
  @HostBinding('class') get classList(): any {
    return {
      'search-appearance-bar': this.appearance === 'bar',
      'search-appearance-basic': this.appearance === 'basic',
      'search-opened': this.opened,
    };
  }

  /**
   * Setter for bar search input
   *
   * @param value
   */
  @ViewChild('barSearchInput')
  set barSearchInput(value: ElementRef) {
    if (value) {
      setTimeout(() => {
        value.nativeElement.focus();
      });
    }
  }

  /**
   * Setter for mat-autocomplete element reference
   *
   * @param value
   */
  @ViewChild('matAutocomplete')
  set matAutocomplete(value: MatAutocomplete) {
    this._matAutocomplete = value;
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  ngOnChanges(changes: SimpleChanges): void {
    if ('appearance' in changes) {
      this.close();
    }
  }

  ngOnInit(): void {
    this.searchControl.valueChanges
      .pipe(
        debounceTime(this.debounce),
        takeUntil(this._unsubscribeAll),
        map(value => {
          if (!value || value.length < this.minLength) {
            this.filteredResults = null;
            this.filteredPages = null;
          }
          return value;
        }),
        filter(value => value && value.length >= this.minLength)
      )
      .subscribe(value => {
        this.filterResults(value);
      });
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  onKeydown(event: KeyboardEvent): void {
    if (event.code === 'Escape') {
      if (this.appearance === 'bar' && !this._matAutocomplete.isOpen) {
        this.close();
      }
    }
  }

  /**
   * Open the search
   * Fetch the data only when the search bar is opened.
   */
  open(): void {
    if (this.opened) {
      return;
    }

    this.opened = true;

    // Fetch data when search bar is opened
    this.fetchData();
  }

  /**
   * Close the search
   */
  close(): void {
    if (!this.opened) {
      return;
    }

    this.searchControl.setValue('');
    this.opened = false;
  }

  /**
   * Fetch data from the API once when the search bar is opened
   */
  private fetchData(): void {
    this._httpClient.post('api/common/search', { query: '' }).subscribe(
      (response: any) => {
        if (response && response.results) {
          this.resultSets = response.results;
          this.initializeFuse();
        } else {
          console.error('Malformed response:', response);
        }
      },
      error => {
        console.error('Data fetch failed:', error);
      }
    );
  }
  /**
   * Initialize Fuse.js for fuzzy searching both shipments/quote requests and pages
   */
  private initializeFuse(): void {
    const fuseOptionsForShipments = {
      keys: [
        'quoteRequestNumber',
        'bookingNumber',
        'commodity',
        'pol',
        'pod',
        'qrStatus',
        'status',
        'enquiryType',
      ],
      threshold: 0.3, // Adjust for desired fuzziness
    };

    // Initialize Fuse for shipments and quote requests
    const allResults = this.resultSets
      .filter(set => set.id !== 'pages') // Exclude pages
      .reduce((acc, set) => acc.concat(set.results), []); // Merge all results into one array
    this.fuseShipmentsAndQuotes = new Fuse(allResults, fuseOptionsForShipments);

    // Initialize Fuse for pages
    const fuseOptionsForPages = {
      keys: ['title', 'link'],
      threshold: 0.3,
    };
    const pages =
      this.resultSets.find(set => set.id === 'pages')?.results || [];
    this.fusePages = new Fuse(pages, fuseOptionsForPages);
  }
  /**
   * Filter results using Fuse.js for fuzzy searching
   */
  private filterResults(query: string): void {
    const lowerCaseQuery = query.toLowerCase();

    // Fuzzy search for shipments and quote requests
    this.filteredResults = this.fuseShipmentsAndQuotes
      .search(query)
      .map((result: any) => result.item);

    // Fuzzy search for pages
    this.filteredPages = this.fusePages
      .search(query)
      .map((result: any) => result.item);

    // Emit the search results
    this.search.next(this.filteredResults);
  }

  trackByFn(index: number, item: any): any {
    return item.id || index;
  }
}
